import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useHospitalOnboarding } from '../../contexts/HospitalOnboardingContext';
import DynamicMonthlyDemandTable, {
  getAmbulanceTypeShortName,
  getAmbulanceTypeKey
} from '../../components/hospitalOnboarding/DynamicMonthlyDemandTable';
import CompactTimeSelect from '../../components/hospitalOnboarding/CompactTimeSelect';
import {
  Mic,
  Calendar,
  Clock,
  Truck,
  ArrowLeft,
  ArrowRight,
  Bookmark,
  Check,
  Percent,
  X
} from 'lucide-react';

const PEAK_TIME_SLOTS = [
  '12 AM–6 AM',
  '6 AM–12 PM',
  '12 PM–6 PM',
  '6 PM–12 AM'
];

const DELAY_REASONS = [
  'No ambulance available',
  'Delayed arrival',
  'Ambulance unavailable',
  'High price',
  'Driver unavailable',
  'Long distance'
];

const TIME_SELECT_OPTIONS = [
  '12:00 AM', '01:00 AM', '02:00 AM', '03:00 AM', '04:00 AM', '05:00 AM',
  '06:00 AM', '07:00 AM', '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM',
  '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM',
  '06:00 PM', '07:00 PM', '08:00 PM', '09:00 PM', '10:00 PM', '11:00 PM'
];

export const Step3AmbulanceRequirements = () => {
  const navigate = useNavigate();
  const {
    formData,
    updateFields,
    saveAndContinue,
    goBack,
    saveAndExit,
    isSaving,
    ambulanceTypes
  } = useHospitalOnboarding();

  // Dynamically map ambulance types from backend lookups (deduplicated by key/shortName)
  const dynamicMixTypes = React.useMemo(() => {
    if (Array.isArray(ambulanceTypes) && ambulanceTypes.length > 0) {
      const seen = new Set();
      const result = [];
      ambulanceTypes.forEach((t) => {
        const short = t.abbreviation || t.name;
        const key = String(short).trim().toUpperCase();
        if (!seen.has(key)) {
          seen.add(key);
          result.push({
            id: t.type_id || t.id || t.name,
            name: t.name,
            shortName: short,
            key: getAmbulanceTypeKey(t)
          });
        }
      });
      return result;
    }
    return [];
  }, [ambulanceTypes]);

  // Helper to parse custom peak time range string e.g. "09:00 AM – 01:00 PM"
  const parseCustomTime = (str = '') => {
    if (!str) return { from: '', to: '' };
    const parts = str.split(/\s*[-–—to]\s*/i).map((s) => s.trim()).filter(Boolean);
    return {
      from: parts[0] || '',
      to: parts[1] || ''
    };
  };

  const { from: fromPeakTime, to: toPeakTime } = parseCustomTime(formData.custom_peak_time);

  const handleCustomTimeChange = (from, to) => {
    if (!from && !to) {
      updateFields({ custom_peak_time: '' });
      return;
    }
    if (from && to) {
      updateFields({ custom_peak_time: `${from} – ${to}` });
    } else if (from) {
      updateFields({ custom_peak_time: from });
    } else {
      updateFields({ custom_peak_time: to });
    }
  };

  const handleTimeSlotToggle = (slot) => {
    const currentSlots = formData.peak_time_slots || [];
    const updated = currentSlots.includes(slot)
      ? currentSlots.filter((s) => s !== slot)
      : [...currentSlots, slot];
    updateFields({ peak_time_slots: updated });
  };

  const handleReasonToggle = (reason) => {
    const currentReasons = formData.unserved_delay_reasons || [];
    const updated = currentReasons.includes(reason)
      ? currentReasons.filter((r) => r !== reason)
      : [...currentReasons, reason];
    updateFields({ unserved_delay_reasons: updated });
  };

  // Ambulance Mix field accessor purely driven by dynamic types
  const getMixPercentValue = (t) => {
    const specificKey = `mix_${t.key}_percent`;
    if (formData[specificKey] !== undefined && formData[specificKey] !== '') {
      return formData[specificKey];
    }
    if (formData.ambulance_mix?.[t.id] !== undefined && formData.ambulance_mix?.[t.id] !== '') {
      return formData.ambulance_mix[t.id];
    }
    return '';
  };

  const handleMixPercentChange = (t, val) => {
    const numVal = val === '' ? '' : Math.max(0, Math.min(100, Number(val)));
    const specificKey = `mix_${t.key}_percent`;
    const updatedMix = {
      ...(formData.ambulance_mix || {}),
      [t.id]: numVal
    };

    updateFields({
      [specificKey]: numVal,
      ambulance_mix: updatedMix
    });
  };

  const totalMixPercentage = dynamicMixTypes.reduce((sum, t) => {
    const val = Number(getMixPercentValue(t));
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  const handleContinue = (e) => {
    e.preventDefault();
    saveAndContinue();
  };

  return (
    <div className="w-full">
      {/* Header Row: Step info and Switch to Voice */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 block mb-1">
            STEP 3 OF 5
          </span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight leading-tight">
            Ambulance requirements
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1 font-normal">
            Estimates are welcome. Share historical volume and operational demand factors.
          </p>
          {/* Smart Ambulance Blue accent line */}
          <div className="w-14 sm:w-16 h-0.5 bg-blue-600 mt-2 rounded-full" />
        </div>

        <button
          type="button"
          onClick={() => navigate('/hospital/onboarding/voice?type=hospital&step=3')}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-xs font-medium text-slate-700 shadow-2xs transition-all cursor-pointer self-start sm:self-auto"
        >
          <Mic className="w-3.5 h-3.5 text-slate-600" />
          <span>Switch to voice</span>
        </button>
      </div>

      <form onSubmit={handleContinue} className="space-y-6">
        {/* Card 1: Last 12 months volume with Dynamic Backend Ambulance Types */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/80 shadow-xs p-6 sm:p-8">
          <DynamicMonthlyDemandTable
            rows={formData.monthly_demands || []}
            ambulanceTypes={ambulanceTypes}
            onChange={(updatedRows) => updateFields({ monthly_demands: updatedRows })}
          />
        </div>

        {/* Card 2: Peak demand */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/80 shadow-xs p-6 sm:p-8">
          <div className="flex items-center gap-3.5 mb-6 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-800 leading-tight">
                Peak demand & turnaround
              </h3>
              <p className="text-xs text-slate-500 mt-0.5 font-normal">
                Surge hours, bottlenecks and common dispatch delays.
              </p>
            </div>
          </div>

          {/* Peak Time Checkbox Pills */}
          <div className="mb-6">
            <label className="block text-xs font-medium text-slate-600 mb-2">
              Peak Time Slots
            </label>
            <div className="flex flex-wrap gap-2 sm:gap-2.5">
              {PEAK_TIME_SLOTS.map((slot) => {
                const isSelected = (formData.peak_time_slots || []).includes(slot);
                return (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => handleTimeSlotToggle(slot)}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs transition-all select-none cursor-pointer ${
                      isSelected
                        ? 'bg-blue-50 border-blue-400 text-blue-700 font-medium shadow-2xs'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 font-normal'
                    }`}
                  >
                    <span
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                        isSelected
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-slate-300 bg-white'
                      }`}
                    >
                      {isSelected && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                    </span>
                    <span>{slot}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom Peak Time (Select Range) & Peak Days */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-medium text-slate-600">
                  Custom Peak Time (Select Range)
                </label>
                {formData.custom_peak_time && (
                  <button
                    type="button"
                    onClick={() => handleCustomTimeChange('', '')}
                    className="text-[11px] text-blue-600 hover:text-blue-800 cursor-pointer font-medium flex items-center gap-0.5"
                  >
                    <X className="w-3 h-3" />
                    <span>Clear</span>
                  </button>
                )}
              </div>

              {/* Two Time Dropdowns: From & To */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <CompactTimeSelect
                    value={fromPeakTime}
                    onChange={(val) => handleCustomTimeChange(val, toPeakTime)}
                    options={TIME_SELECT_OPTIONS}
                    placeholder="Start time (From)"
                  />
                </div>
                <div>
                  <CompactTimeSelect
                    value={toPeakTime}
                    onChange={(val) => handleCustomTimeChange(fromPeakTime, val)}
                    options={TIME_SELECT_OPTIONS}
                    placeholder="End time (To)"
                  />
                </div>
              </div>

              {formData.custom_peak_time && (
                <p className="text-[11px] text-blue-600 mt-1 font-medium">
                  Selected: <span className="font-semibold text-slate-800">{formData.custom_peak_time}</span>
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Peak Days
              </label>
              <select
                value={formData.peak_days || ''}
                onChange={(e) => updateFields({ peak_days: e.target.value })}
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 cursor-pointer"
              >
                <option value="">Select an option</option>
                <option value="All days">All days</option>
                <option value="Monday - Friday (Weekdays)">Monday - Friday (Weekdays)</option>
                <option value="Saturday - Sunday (Weekends)">Saturday - Sunday (Weekends)</option>
                <option value="Festival / Special days">Festival / Special days</option>
              </select>
            </div>
          </div>

          {/* Approx. Peak Calls / Hour */}
          <div className="mb-6">
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Approx. Peak Calls / Hour
            </label>
            <input
              type="text"
              placeholder="Enter peak calls / hour"
              value={formData.approx_peak_calls_per_hour || ''}
              onChange={(e) => updateFields({ approx_peak_calls_per_hour: e.target.value })}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
            />
          </div>

          {/* Reasons for Unserved / Delayed Requests Pills */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-600 mb-2">
              Common Reasons for Unserved / Delayed Requests
            </label>
            <div className="flex flex-wrap gap-2 sm:gap-2.5">
              {DELAY_REASONS.map((reason) => {
                const isSelected = (formData.unserved_delay_reasons || []).includes(reason);
                return (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => handleReasonToggle(reason)}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs transition-all select-none cursor-pointer ${
                      isSelected
                        ? 'bg-blue-50 border-blue-400 text-blue-700 font-medium shadow-2xs'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 font-normal'
                    }`}
                  >
                    <span
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                        isSelected
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-slate-300 bg-white'
                      }`}
                    >
                      {isSelected && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                    </span>
                    <span>{reason}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Other Reason */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Other Specific Reason
            </label>
            <input
              type="text"
              placeholder="Enter other specific reason"
              value={formData.unserved_delay_other_reason || ''}
              onChange={(e) => updateFields({ unserved_delay_other_reason: e.target.value })}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
            />
          </div>
        </div>

        {/* Card 3: Dynamic Ambulance Requirement Mix from Backend Types (Hidden as requested) */}
        {/*
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/80 shadow-xs p-6 sm:p-8">
          <div className="flex items-center justify-between gap-3.5 mb-6 pb-4 border-b border-slate-100 flex-wrap">
            <div className="flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800 leading-tight">
                  Ambulance requirement mix
                </h3>
                <p className="text-xs text-slate-500 mt-0.5 font-normal">
                  Approximate distribution of required vehicle types.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span
                className={`text-xs font-semibold px-3 py-1 rounded-full border ${
                  totalMixPercentage === 100
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : totalMixPercentage > 100
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : totalMixPercentage > 0
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-slate-50 text-slate-600 border-slate-200'
                }`}
              >
                {totalMixPercentage === 100
                  ? '✓ Total: 100% (Balanced)'
                  : totalMixPercentage > 100
                  ? `Total: ${totalMixPercentage}% (Exceeds by ${totalMixPercentage - 100}%)`
                  : totalMixPercentage > 0
                  ? `Total: ${totalMixPercentage}% (${100 - totalMixPercentage}% remaining)`
                  : 'Total: 0%'}
              </span>
            </div>
          </div>

          {dynamicMixTypes.length === 0 ? (
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-slate-500 text-xs flex items-center gap-2 mb-4">
              <span className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
              <span>Loading verified ambulance categories from server...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {dynamicMixTypes.map((t) => {
                const currentVal = getMixPercentValue(t);
                return (
                  <div key={t.id}>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      {t.name} ({t.shortName}) %
                    </label>
                    <div className="relative">
                      <Percent className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Enter percentage"
                        value={currentVal}
                        onChange={(e) => handleMixPercentChange(t, e.target.value)}
                        className="w-full pr-10 pl-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        */}

        {/* Bottom Action Buttons */}
        <div className="flex items-center justify-between pt-2 pb-12">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer shadow-2xs"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={saveAndExit}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer shadow-2xs"
            >
              <Bookmark className="w-4 h-4 text-slate-400" />
              <span>Save & exit</span>
            </button>

            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium shadow-xs transition-colors cursor-pointer"
            >
              <span>Save & continue</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Step3AmbulanceRequirements;
